import React, { useState, useEffect } from 'react';
import { ContractRecipient, ContractRecipientCreate } from '../types';
import { authService } from '../services/authService';

interface ContractRecipientFormProps {
  contractId: string;
  initialData?: ContractRecipient | null;
  onSubmit: (data: ContractRecipientCreate) => void;
  onCancel: () => void;
}

const ContractRecipientForm: React.FC<ContractRecipientFormProps> = ({
  contractId,
  initialData,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState<ContractRecipientCreate>({
    contract_id: contractId,
    recipient_type: 'to',
    email: '',
    name: '',
    is_contract: true,
    is_invoice: true,
    created_by: authService.getUser()?.username || 'system'
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        contract_id: contractId,
        recipient_type: initialData.recipient_type,
        email: initialData.email,
        name: initialData.name || '',
        is_contract: initialData.is_contract,
        is_invoice: initialData.is_invoice,
        created_by: authService.getUser()?.username || 'system'
      });
    }
  }, [initialData, contractId]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.recipient_type) {
      newErrors.recipient_type = 'Recipient type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof ContractRecipientCreate, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="recipient_type">Recipient Type *</label>
        <select
          id="recipient_type"
          value={formData.recipient_type}
          onChange={(e) => handleInputChange('recipient_type', e.target.value as 'to' | 'cc' | 'bcc')}
          className={`form-control ${errors.recipient_type ? 'error' : ''}`}
          required
        >
          <option value="to">To (Primary Recipient)</option>
          <option value="cc">CC (Carbon Copy)</option>
          <option value="bcc">BCC (Blind Carbon Copy)</option>
        </select>
        {errors.recipient_type && <span className="error-message">{errors.recipient_type}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="email">Email Address *</label>
        <input
          type="email"
          id="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          className={`form-control ${errors.email ? 'error' : ''}`}
          placeholder="Enter email address"
          required
        />
        {errors.email && <span className="error-message">{errors.email}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="name">Name (Optional)</label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className="form-control"
          placeholder="Enter recipient name"
        />
      </div>

      <div className="form-group">
        <label>Communication Preferences</label>
        <div style={{ marginTop: '10px' }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.is_contract}
                onChange={(e) => handleInputChange('is_contract', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Receive contract updates and notifications
            </label>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.is_invoice}
                onChange={(e) => handleInputChange('is_invoice', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Receive invoice communications
            </label>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button type="submit" className="btn btn-primary">
          {initialData ? 'Update Recipient' : 'Add Recipient'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ContractRecipientForm;